/**
 * Elegir de qué agentes sale el PDF (Armando, 24-sep-2026: "le dé la opción
 * de seleccionar de qué agentes quieres descargar la información y de quiénes
 * no").
 *
 * Arranca con los que están en ROJO el último día marcados: es para quienes
 * el supervisor más probablemente quiere el papel. Si nadie está en rojo,
 * arranca con todos.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';

import { TONO, f1, filtrosAQuery } from '../inicio/comun';

const NEUTRO = '#6b7280';
const CHIP = {
  padding: '6px 12px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.14)', background: 'transparent',
  color: '#e6e6ec', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit',
};

export default function ElegirAgentesPdf({ agentes, filtros, onCerrar }) {
  const inicial = useMemo(() => {
    const rojos = agentes.filter((a) => a.ultimo_dia.senal === 'detente').map((a) => a.agente);
    return new Set(rojos.length ? rojos : agentes.map((a) => a.agente));
  }, [agentes]);
  const [sel, setSel] = useState(inicial);
  const [portada, setPortada] = useState(true);
  const [busca, setBusca] = useState('');
  const dialogo = useRef(null);

  // Escape cierra, y el foco entra al diálogo al abrir.
  useEffect(() => {
    dialogo.current?.focus();
    const tecla = (e) => { if (e.key === 'Escape') onCerrar(); };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [onCerrar]);

  const marcar = (nombres) => setSel(new Set(nombres));
  const por = (senal) => agentes.filter((a) => a.ultimo_dia.senal === senal).map((a) => a.agente);
  const alternar = (nombre) => {
    const s = new Set(sel);
    if (s.has(nombre)) s.delete(nombre); else s.add(nombre);
    setSel(s);
  };
  const visibles = agentes.filter((a) => a.agente.toLowerCase().includes(busca.trim().toLowerCase()));
  const n = sel.size;
  const paginas = n + (portada && n ? 1 : 0);

  const descargar = () => {
    // En el orden de la lista (peor primero), no en el orden en que se marcaron.
    const q = new URLSearchParams();
    agentes.filter((a) => sel.has(a.agente)).forEach((a) => q.append('agente', a.agente));
    if (!portada) q.set('portada', '0');
    // El documento sale con los MISMOS filtros que la pantalla que lo pidió.
    new URLSearchParams(filtrosAQuery(filtros || {})).forEach((v, k) => q.append(k, v));
    window.open(`/equipo/pdf?${q.toString()}`, '_blank', 'noopener');
    onCerrar();
  };

  return (
    <div role="presentation" onClick={onCerrar}
         style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(5,5,8,0.62)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-labelledby="titulo-pdf-equipo" tabIndex={-1} ref={dialogo}
           onClick={(e) => e.stopPropagation()}
           style={{ width: 'min(640px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
                    background: '#17171f', border: '1px solid #30303c', borderRadius: 16, outline: 'none',
                    boxShadow: '0 30px 80px rgba(0,0,0,.55)', fontVariantNumeric: 'tabular-nums' }}>
        <div style={{ padding: '20px 22px 12px', borderBottom: '1px solid #26262f' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 id="titulo-pdf-equipo" style={{ margin: 0, fontSize: 18, color: '#e6e6ec' }}>Descargar PDF de agentes</h2>
            <button type="button" aria-label="Cerrar" onClick={onCerrar}
                    style={{ width: 36, height: 36, border: 'none', background: 'transparent', color: '#9a9aa8', cursor: 'pointer' }}>
              <X size={18} />
            </button>
          </div>
          <p style={{ margin: '4px 0 0', color: '#9a9aa8', fontSize: 13 }}>
            Elige de quiénes quieres el reporte. Cada agente ocupa una página, con los mismos datos que ve en su Inicio.
          </p>
        </div>

        <div style={{ padding: '12px 22px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#7a7a88' }}>Seleccionar</span>
          <button type="button" style={CHIP} onClick={() => marcar(agentes.map((a) => a.agente))}>Todos</button>
          <button type="button" style={{ ...CHIP, borderColor: 'rgba(248,113,113,.5)', color: '#fca5a5' }} onClick={() => marcar(por('detente'))}>Sólo en rojo</button>
          <button type="button" style={{ ...CHIP, borderColor: 'rgba(251,191,36,.45)', color: '#fcd34d' }} onClick={() => marcar(por('revisa'))}>Sólo en ámbar</button>
          <button type="button" style={CHIP} onClick={() => marcar([])}>Ninguno</button>
        </div>

        <div style={{ padding: '0 22px' }}>
          <input type="search" className="filter-input" placeholder="Buscar agente" aria-label="Buscar agente"
                 value={busca} onChange={(e) => setBusca(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
        </div>

        <div style={{ margin: '10px 22px 0', overflowY: 'auto', border: '1px solid #26262f', borderRadius: 10, minHeight: 120 }}>
          {visibles.map((a) => (
            <label key={a.agente}
                   style={{ display: 'grid', gridTemplateColumns: '22px 12px 1fr auto auto', gap: 10, alignItems: 'center',
                            padding: '0 14px', minHeight: 44, borderBottom: '1px solid #22222b', cursor: 'pointer',
                            fontSize: 13, color: '#e6e6ec' }}>
              <input type="checkbox" checked={sel.has(a.agente)} onChange={() => alternar(a.agente)}
                     style={{ width: 17, height: 17, accentColor: '#818cf8', cursor: 'pointer' }} />
              <i style={{ width: 10, height: 10, borderRadius: '50%', background: TONO[a.ultimo_dia.senal] || NEUTRO, display: 'block' }} />
              <span style={{ fontWeight: 600 }}>{a.agente}</span>
              <span style={{ color: '#9a9aa8' }}>
                último día <b style={{ color: TONO[a.ultimo_dia.senal] || '#9a9aa8' }}>{f1(a.ultimo_dia.nota)}</b>
              </span>
              <span style={{ color: '#9a9aa8', minWidth: 70, textAlign: 'right' }}>30 d <b style={{ color: '#e6e6ec' }}>{f1(a.global.nota)}</b></span>
            </label>
          ))}
          {visibles.length === 0 && <div style={{ padding: 14, color: '#7a7a88', fontSize: 13 }}>Ningún agente coincide.</div>}
        </div>

        <div style={{ padding: '12px 22px', display: 'grid', gap: 4 }}>
          <label style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, color: '#e6e6ec', cursor: 'pointer', minHeight: 32 }}>
            <input type="checkbox" checked={portada} onChange={() => setPortada(!portada)}
                   style={{ width: 17, height: 17, accentColor: '#818cf8' }} />
            Incluir portada del equipo (resumen y tabla de los seleccionados)
          </label>
          <span style={{ fontSize: 12, color: '#7a7a88' }}>
            Periodo: últimos 30 días, hasta ayer. Las páginas van de la nota más baja a la más alta.
          </span>
        </div>

        <div style={{ padding: '12px 22px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      gap: 12, borderTop: '1px solid #26262f' }}>
          <span aria-live="polite" style={{ fontSize: 13, color: '#c8c8d0' }}>
            <b style={{ color: '#e6e6ec' }}>{n}</b> seleccionado{n === 1 ? '' : 's'} · {paginas} página{paginas === 1 ? '' : 's'}
          </span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn-secondary" onClick={onCerrar}>Cancelar</button>
            <button type="button" className="btn-primary" disabled={n === 0} onClick={descargar}>Descargar PDF</button>
          </div>
        </div>
      </div>
    </div>
  );
}
